import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Send, ThumbsUp, ThumbsDown, X, Loader } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import genemobotSvg from '../images/genemobot.svg'

interface PublicSettings {
  enabled: boolean
  welcomeMessage: string
  botName: string
  botSubtitle: string
}

interface ChatMessage {
  id: number                  // logId for assistant, user-local id (negative) for user
  role: 'user' | 'assistant' | 'welcome'
  content: string
  feedback?: 'good' | 'bad' | null
  loading?: boolean
}

const SESSION_STORAGE_PREFIX = 'genemo_chat_session:'  // ログインユーザー毎に分離
const BTN_POS_STORAGE_PREFIX = 'genemo_chat_btn_pos:'  // ボタン位置（ドラッグ）
const MAX_INPUT_LENGTH = 2000
const DRAG_THRESHOLD_PX = 3   // これ以下はクリック扱い
const EDGE_MARGIN = 8         // 端からこれ以上離す

function generateSessionId(): string {
  const ts = Date.now().toString(36)
  const hex = Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map(b => b.toString(16).padStart(2, '0')).join('')
  return `sess_${ts}${hex}`
}

function getOrCreateSessionId(userId: string): string {
  const key = SESSION_STORAGE_PREFIX + userId
  try {
    const existing = localStorage.getItem(key)
    if (existing && /^sess_[a-zA-Z0-9_-]{6,64}$/.test(existing)) return existing
    const fresh = generateSessionId()
    localStorage.setItem(key, fresh)
    return fresh
  } catch {
    return generateSessionId()
  }
}

function loadButtonPos(userId: string): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(BTN_POS_STORAGE_PREFIX + userId)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed
    return null
  } catch {
    return null
  }
}

function saveButtonPos(userId: string, pos: { x: number; y: number }): void {
  try {
    localStorage.setItem(BTN_POS_STORAGE_PREFIX + userId, JSON.stringify(pos))
  } catch {}
}

function clampToViewport(pos: { x: number; y: number }, size: { w: number; h: number }): { x: number; y: number } {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - size.w - EDGE_MARGIN)
  const maxY = Math.max(EDGE_MARGIN, window.innerHeight - size.h - EDGE_MARGIN)
  return {
    x: Math.min(maxX, Math.max(EDGE_MARGIN, pos.x)),
    y: Math.min(maxY, Math.max(EDGE_MARGIN, pos.y)),
  }
}

export const ChatWidget: React.FC = () => {
  const { user } = useAuth()
  const [settings, setSettings] = useState<PublicSettings | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const sessionId = useMemo(() => user ? getOrCreateSessionId(user.userId) : '', [user])
  const scrollRef = useRef<HTMLDivElement>(null)
  const userLocalIdRef = useRef<number>(-1)

  // 浮遊ボタンのドラッグ位置
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonPos, setButtonPos] = useState<{ x: number; y: number } | null>(null)
  const dragStateRef = useRef<{ startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null)
  const justDraggedRef = useRef<boolean>(false)

  // 公開設定を取得
  useEffect(() => {
    let cancelled = false
    fetch('/api/chatbot/settings-public', { credentials: 'include' })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return
        if (json.success) setSettings(json.data)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // 浮遊ボタンの初期位置を計算（localStorage から復元 or 右下デフォルト）
  useEffect(() => {
    if (!user || !settings?.enabled) return
    const saved = loadButtonPos(user.userId)
    // 実サイズは次のフレームで測定したいので、初期描画を走らせてから計算
    requestAnimationFrame(() => {
      const btn = buttonRef.current
      const w = btn?.offsetWidth ?? 170
      const h = btn?.offsetHeight ?? 68
      if (saved) {
        setButtonPos(clampToViewport(saved, { w, h }))
      } else {
        setButtonPos({
          x: window.innerWidth - w - 24,
          y: window.innerHeight - h - 24,
        })
      }
    })
  }, [user, settings?.enabled])

  // ウィンドウリサイズでボタンを画面内にクランプ
  useEffect(() => {
    const onResize = () => {
      const btn = buttonRef.current
      if (!btn) return
      const w = btn.offsetWidth
      const h = btn.offsetHeight
      setButtonPos(prev => prev ? clampToViewport(prev, { w, h }) : prev)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // グローバルなマウス移動・マウスアップ・タッチのハンドラ
  useEffect(() => {
    const handleMove = (clientX: number, clientY: number) => {
      const ds = dragStateRef.current
      if (!ds) return
      const dx = clientX - ds.startX
      const dy = clientY - ds.startY
      if (!ds.moved && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
        ds.moved = true
      }
      if (ds.moved) {
        const btn = buttonRef.current
        const w = btn?.offsetWidth ?? 170
        const h = btn?.offsetHeight ?? 68
        setButtonPos(clampToViewport({ x: ds.origX + dx, y: ds.origY + dy }, { w, h }))
      }
    }
    const handleEnd = () => {
      const ds = dragStateRef.current
      if (ds && ds.moved) {
        justDraggedRef.current = true
        if (user) {
          setButtonPos(prev => {
            if (prev) saveButtonPos(user.userId, prev)
            return prev
          })
        }
        // 直後の click で「開く」にならないよう一瞬ブロック
        setTimeout(() => { justDraggedRef.current = false }, 50)
      }
      dragStateRef.current = null
    }
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY)
    const onTouchMove = (e: TouchEvent) => {
      if (!dragStateRef.current) return
      const t = e.touches[0]
      if (!t) return
      if (dragStateRef.current.moved) e.preventDefault()
      handleMove(t.clientX, t.clientY)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', handleEnd)
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', handleEnd)
    document.addEventListener('touchcancel', handleEnd)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', handleEnd)
      document.removeEventListener('touchcancel', handleEnd)
    }
  }, [user])

  const handleButtonMouseDown = (clientX: number, clientY: number) => {
    if (!buttonPos) return
    dragStateRef.current = {
      startX: clientX,
      startY: clientY,
      origX: buttonPos.x,
      origY: buttonPos.y,
      moved: false,
    }
  }

  const handleButtonClick = () => {
    if (justDraggedRef.current) return
    setIsOpen(true)
  }

  // ウェルカムメッセージの初期化
  useEffect(() => {
    if (isOpen && settings && messages.length === 0) {
      setMessages([{
        id: 0,
        role: 'welcome',
        content: settings.welcomeMessage,
      }])
    }
  }, [isOpen, settings, messages.length])

  // 新しいメッセージで自動スクロール
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || sending || !sessionId) return
    if (text.length > MAX_INPUT_LENGTH) {
      alert(`メッセージは ${MAX_INPUT_LENGTH} 文字以内で入力してください`)
      return
    }

    const userLocalId = userLocalIdRef.current--
    setMessages(prev => [
      ...prev,
      { id: userLocalId, role: 'user', content: text },
      { id: userLocalId - 0.5, role: 'assistant', content: '', loading: true },
    ])
    setInput('')
    setSending(true)

    try {
      const res = await fetch('/api/chatbot/message', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, sessionId }),
      })
      const json = await res.json()
      if (!res.ok || !json.success) {
        setMessages(prev => prev.map(m => m.loading ? {
          id: m.id,
          role: 'assistant',
          content: `エラー: ${json.error || '応答取得に失敗しました'}`,
          loading: false,
        } : m))
      } else {
        const { reply, logId } = json.data as { reply: string; logId: number }
        setMessages(prev => prev.map(m => m.loading ? {
          id: logId,
          role: 'assistant',
          content: reply,
          loading: false,
        } : m))
      }
    } catch (err) {
      setMessages(prev => prev.map(m => m.loading ? {
        id: m.id,
        role: 'assistant',
        content: `通信エラー: ${err instanceof Error ? err.message : String(err)}`,
        loading: false,
      } : m))
    } finally {
      setSending(false)
    }
  }, [input, sending, sessionId])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleFeedback = async (logId: number, feedback: 'good' | 'bad') => {
    if (logId <= 0) return  // user-local id, can't rate
    // Toggle: if already set, clear
    const current = messages.find(m => m.id === logId)?.feedback
    const next: 'good' | 'bad' | null = current === feedback ? null : feedback
    setMessages(prev => prev.map(m => m.id === logId ? { ...m, feedback: next } : m))
    try {
      await fetch('/api/chatbot/feedback', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logId, feedback: next }),
      })
    } catch {
      // 失敗時のロールバックは省略（UI 表示だけ）
    }
  }

  if (!settings?.enabled) return null

  return (
    <>
      {/* 浮遊ボタン（閉じているとき、ドラッグで移動可） */}
      {!isOpen && (
        <button
          ref={buttonRef}
          onMouseDown={e => handleButtonMouseDown(e.clientX, e.clientY)}
          onTouchStart={e => {
            const t = e.touches[0]
            if (t) handleButtonMouseDown(t.clientX, t.clientY)
          }}
          onClick={handleButtonClick}
          aria-label="チャットで質問する"
          className="fixed z-125 flex items-center gap-2 pl-2 pr-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white shadow-overlay transition-colors cursor-grab active:cursor-grabbing select-none"
          style={
            buttonPos
              ? { left: `${buttonPos.x}px`, top: `${buttonPos.y}px`, visibility: 'visible', color: '#ffffff' }
              : { right: '24px', bottom: '24px', visibility: 'hidden', color: '#ffffff' }
          }
        >
          <img src={genemobotSvg} alt="" className="w-11 h-11 pointer-events-none" draggable={false} />
          <span
            className="text-sm font-bold leading-tight pointer-events-none"
            style={{ color: '#ffffff' }}
          >
            CHATで<br/>質問
          </span>
        </button>
      )}

      {/* チャットモーダル */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-125 w-[380px] max-w-[calc(100vw-24px)] h-[620px] max-h-[calc(100vh-104px)] flex flex-col bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* ヘッダー */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-white">
            <div className="flex items-center gap-3 min-w-0">
              <img src={genemobotSvg} alt="" className="w-14 h-14 shrink-0" />
              <div className="min-w-0">
                <div className="font-bold truncate" style={{ color: '#ffffff' }}>{settings.botName}</div>
                <div className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.9)' }}>{settings.botSubtitle}</div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} aria-label="閉じる" className="p-2 rounded-full bg-white/15 hover:bg-white/30 active:bg-white/40 transition-all shrink-0 shadow-inner border border-white/20">
              <X className="w-4 h-4 drop-shadow-sm" color="white" strokeWidth={2.5} />
            </button>
          </div>

          {/* メッセージ一覧 */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 dark:bg-slate-900/40">
            {messages.map(m => (
              <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex items-start gap-2'}>
                {m.role !== 'user' && (
                  <img src={genemobotSvg} alt="" className="w-8 h-8 shrink-0 mt-0.5" />
                )}
                <div className={m.role === 'user'
                  ? 'max-w-[80%] px-3 py-2 rounded-2xl bg-sky-500 text-white text-sm whitespace-pre-wrap wrap-break-word'
                  : 'max-w-[85%] px-3 py-2 rounded-2xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm border border-slate-200 dark:border-slate-600 whitespace-pre-wrap wrap-break-word'
                }>
                  {m.loading ? (
                    <div className="flex items-center gap-1.5 py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : m.content}
                  {/* フィードバック（assistant のみ、かつ保存済みログ） */}
                  {m.role === 'assistant' && !m.loading && m.id > 0 && (
                    <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-slate-200 dark:border-slate-600">
                      <button
                        onClick={() => handleFeedback(m.id, 'good')}
                        className={`p-1 rounded-sm hover:bg-slate-100 dark:hover:bg-slate-600 ${m.feedback === 'good' ? 'text-emerald-500' : 'text-slate-400'}`}
                        aria-label="役に立った"
                      >
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleFeedback(m.id, 'bad')}
                        className={`p-1 rounded-sm hover:bg-slate-100 dark:hover:bg-slate-600 ${m.feedback === 'bad' ? 'text-red-500' : 'text-slate-400'}`}
                        aria-label="役に立たなかった"
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 免責 + 入力欄 */}
          <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <p className="px-4 pt-2 text-[10px] text-center text-slate-400">
              AIの回答は正確でない場合があります。<br/>
              <a href="/manual" target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline">マニュアル</a>もあわせてご活用ください。
            </p>
            <div className="flex items-end gap-2 p-3">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="質問を入力..."
                rows={1}
                maxLength={MAX_INPUT_LENGTH}
                className="flex-1 resize-none border border-slate-300 dark:border-slate-600 rounded-2xl px-3 py-2 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-hidden focus:ring-2 focus:ring-sky-500 text-sm max-h-32"
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={sending || !input.trim()}
                aria-label="送信"
                className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white transition-colors"
              >
                {sending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
